import { ViewRoom, ViewTable } from '@xeetr/types/models'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import TableCardHeader from './TableCardHeader'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { DraggableAttributes } from '@dnd-kit/core'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'

interface Props {
  tableIndex: number
  table: ViewTable
  dragProps: DraggableAttributes | SyntheticListenerMap
}

const TableCard = ({ tableIndex, table, dragProps }: Props) => {
  const { control, setValue } = useFormContext<ViewRoom>()
  const roomTables = useWatch({ control, name: 'tables' })
  const otherTables = roomTables?.filter((t) => t.id !== table.id) || []

  return (
    <Card>
      <CardHeader>
        {/* Pass dragProps explicitly to your Header component */}
        <TableCardHeader table={table} tableIndex={tableIndex} dragProps={dragProps} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label htmlFor="tableName" className="mb-1 block text-sm font-medium">
            Name
          </label>
          <Controller
            name={`tables.${tableIndex}.name`}
            control={control}
            render={({ field }) => (
              <Input
                className="w-full"
                id="tableName"
                value={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="flex gap-1">
          <div>
            <label htmlFor="tableMinCapacity" className="block text-sm font-medium">
              Min. Sitzplätze
            </label>
            <Controller
              name={`tables.${tableIndex}.minCapacity`}
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  className="w-full"
                  id="tableMinCapacity"
                  value={field.value ?? table.size}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              )}
            />
          </div>
          <div>
            <label htmlFor="tableCapacity" className="block text-sm font-medium">
              Max. Sitzplätze
            </label>
            <Controller
              name={`tables.${tableIndex}.maxCapacity`}
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  className="w-full"
                  id="tableCapacity"
                  value={field.value ?? table.size}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0
                    field.onChange(val)
                    setValue(`tables.${tableIndex}.size`, val)
                  }}
                />
              )}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Kombinierbare Tische</label>
          <Controller
            name={`tables.${tableIndex}.combinableTables`}
            control={control}
            render={({ field }) => {
              const selectedIds = field.value?.map((t: ViewTable) => t.id) || []
              return (
                <MultiSelect
                  options={otherTables.map((t) => ({ label: t.name, value: t.id }))}
                  value={selectedIds}
                  onValueChange={(ids) => {
                    const newTables = ids.map((id) => ({ id }) as ViewTable)
                    field.onChange(newTables)
                  }}
                  placeholder="Tische wählen"
                />
              )
            }}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default TableCard
